import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';

function DetailAvatar({ src, name }) {
  const [broken, setBroken] = useState(false);
  const initial = (name && name.trim()[0]) || '?';
  if (src && !broken) {
    return (
      <img className="emp-detail-hero-avatar" src={src} alt="" onError={() => setBroken(true)} />
    );
  }
  return (
    <span className="emp-detail-hero-avatar emp-detail-hero-avatar--placeholder" aria-hidden="true">
      {initial.toUpperCase()}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="emp-detail-row">
      <dt>{label}</dt>
      <dd>{value != null && value !== '' ? value : '—'}</dd>
    </div>
  );
}

function EmployeeDetailPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = Number(employeeId);
    if (!Number.isInteger(id) || id < 1) {
      setError('Invalid employee');
      setLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await webApi.getEmployee(id, { signal: ac.signal });
        if (ac.signal.aborted) return;
        setEmployee(data);
      } catch (err) {
        if (ac.signal.aborted || isCanceledRequest(err)) return;
        setError(err.message || 'Could not load employee');
        setEmployee(null);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [employeeId]);

  const ratingText =
    employee?.rating != null && !Number.isNaN(Number(employee.rating))
      ? Number(employee.rating).toFixed(1)
      : '—';

  return (
    <div className="emp-page emp-page--detail">
      <header className="emp-topbar">
        <button type="button" className="emp-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="emp-topbar-title">Employee Details</h1>
        <span className="emp-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="emp-detail-body">
        {loading ? (
          <p className="emp-loading">Loading…</p>
        ) : error ? (
          <div className="emp-empty">
            <p className="emp-empty-title">{error}</p>
            <button type="button" className="emp-manage-btn emp-manage-btn--primary" onClick={() => navigate('/employees')}>
              Back to employees
            </button>
          </div>
        ) : employee ? (
          <>
            <div className="emp-detail-hero">
              <DetailAvatar src={employee.profilePictureUrl} name={employee.name} />
              <p className="emp-detail-hero-name">{employee.name || '—'}</p>
            </div>

            <article className="emp-detail-card">
              <h2 className="emp-detail-card-title">Personal Details</h2>
              <dl className="emp-detail-dl">
                <DetailRow label="Phone Number" value={employee.phone} />
                <DetailRow label="Email ID" value={employee.email} />
                <DetailRow label="City" value={employee.city} />
                <DetailRow label="State" value={employee.state} />
                <DetailRow label="Designation" value={employee.designation} />
                <DetailRow label="Rating" value={ratingText} />
              </dl>
            </article>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default EmployeeDetailPage;
